export const ProductType = {
  NEO_BANK: 'NEO_BANK',
  CDT: 'CDT',
} as const

export type ProductType = typeof ProductType[keyof typeof ProductType]

export const PaymentFrequency = {
  DAILY: 'DAILY',
  MONTHLY: 'MONTHLY',
  AT_MATURITY: 'AT_MATURITY',
} as const

export type PayoutFrequency = typeof PaymentFrequency[keyof typeof PaymentFrequency]

export type ProductCategory = 'SAVINGS' | 'CDT'

export interface Product {
  product_id: string
  display_name: string
  category: ProductCategory
  rate_ea: number
  term_days: number
  payout_frequency: PayoutFrequency
  min_amount: number
  is_gmf_exemptible: boolean
  management_fee: number
  source_url: string
  last_rate_update: string
}

export interface Entity {
  entity_name: string
  entity_type: 'NEO_BANK' | 'TRADITIONAL'
  products: Product[]
}

export interface TaxConstants {
  uvt_value: number
  savings_retention_threshold_uvt_daily: number
  savings_retention_rate: number
  cdt_retention_rate: number
  cdt_retention_threshold: number
}

export interface Metadata {
  country: string
  currency: string
  last_updated: string
  tax_constants: TaxConstants
}

export interface CountryData {
  metadata: Metadata
  entities: Entity[]
}

export const AllowedCountries = {
  COLOMBIA: 'Colombia',
} as const

export type Country = typeof AllowedCountries[keyof typeof AllowedCountries]

export type FinancialData = Record<Country, CountryData>

// Convenience type used in the UI (flattened bank + best product)
export interface BankView {
  name: string
  rate: number // rate_ea as percentage (e.g. 12.5 for 12.5%)
  entity: Entity
  product: Product
}

export type LoadingSize = 'sm' | 'lg'

export const COMMON_ALLOWED_KEYS = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab']

/**
 * Derives a flat list of BankView items from the new API format.
 * Each entity contributes one entry using its best product for the given category.
 */
export function getBankViews(data: FinancialData, country: Country, category: ProductCategory = 'SAVINGS'): BankView[] {
  const countryData = data[country]
  if (!countryData) return []

  return countryData.entities
    .map((entity) => {
      const categoryProducts = entity.products.filter((p) => p.category === category)
      const bestProduct =
        categoryProducts.length > 0 ? categoryProducts.reduce((best, p) => (p.rate_ea > best.rate_ea ? p : best)) : null

      if (!bestProduct) return null

      return {
        name: entity.entity_name,
        rate: +(bestProduct.rate_ea * 100).toFixed(4), // convert decimal to %
        entity,
        product: bestProduct,
      } as BankView
    })
    .filter((b): b is BankView => b !== null)
}

/**
 * Returns entities that have at least one product in the specified category.
 */
export function getEntitiesByCategory(data: FinancialData, country: Country, category: ProductCategory): Entity[] {
  const countryData = data[country]
  if (!countryData) return []

  return countryData.entities.filter((entity) => entity.products.some((p) => p.category === category))
}
